import React from 'react';

import { Checkbox, Table, Tooltip } from '@mantine/core';

import { usePatientListFeatureFlag } from '@/hooks/usePatientListFeatureFlag';

import { PatientData } from '@/types/patientList';

import {
  ClinicCell as ClinicCellV1,
  CurrentReportCell as CurrentReportCellV1,
  OptionsCell as OptionsCellV1,
  PatientCell as PatientCellV1,
  PreviousReportCell as PreviousReportCellV1,
  StatusCell as StatusCellV1,
  TransmissionDateCell as TransmissionDateCellV1
} from '../cells';
import {
  ClinicCell,
  CurrentReportCell,
  DemographicsCell,
  DeviceCell,
  DiagnosisCell,
  FollowingCell,
  InsuranceCell,
  MonitoringCell,
  OptionsCell,
  PatientCell,
  PreviousReportCell,
  SetupCell,
  StatusCell,
  TransmissionDateCell
} from '../cellsV2';
import { ColumnDef, Density } from '../hooks/useColumnPreferences';
import { CHECKLIST_COLUMN_IDS, useRowChecklist } from '../hooks/useRowChecklist';

import styles from './PatientTableRow.module.scss';

interface PatientRowProps {
  patient: PatientData;
  isSelected: boolean;
  onToggle: (patient: PatientData) => void;
  columns?: ColumnDef[];
  density?: Density;
}

// v1 fallback: matches the v1 header's fixed columns.
const V1_COLUMNS: ColumnDef[] = [
  { id: 'status', label: 'Status', width: 200, visible: true },
  { id: 'clinic', label: 'Clinic', width: 200, visible: true },
  { id: 'lastTransmission', label: 'Last Transmission', width: 200, visible: true },
  { id: 'lastCompletedReport', label: 'Last Completed Report', width: 300, visible: true },
  { id: 'openReport', label: 'Most Recent Report', width: 420, visible: true }
];

export const PatientTableRow: React.FC<PatientRowProps> = ({
  patient,
  isSelected,
  onToggle,
  columns,
  density = 'comfortable'
}) => {
  const { isPatientListV2Enabled } = usePatientListFeatureFlag();
  const hasReports = !!patient.previousReport;
  const isV2 = isPatientListV2Enabled && !!columns;
  // v2 padding comes from the row class (POC 12px 16px, compact 7px 12px).
  const cellPy = isV2 ? undefined : density === 'compact' ? 6 : 12;

  const visibleColumns: ColumnDef[] = columns?.filter((c) => c.visible) ?? V1_COLUMNS;

  // One checklist evaluation per row, shared by Setup and the checklist columns.
  const hasChecklistColumn =
    isV2 && visibleColumns.some((c) => CHECKLIST_COLUMN_IDS.includes(c.id));
  const { ref: rowRef, checklist } = useRowChecklist(
    patient.id,
    patient.status,
    hasChecklistColumn
  );

  const renderColumn = (col: ColumnDef) => {
    switch (col.id) {
      case 'status':
        return (
          <Table.Td key={col.id} py={cellPy}>
            {isPatientListV2Enabled ? (
              <StatusCell
                status={patient.status}
                statusReason={patient.statusReason}
                statusOtherReason={patient.statusOtherReason}
                statusEndDate={patient.statusEndDate}
                statusStartDate={patient.statusStartDate}
                statusUpdatedAt={patient.statusUpdatedAt}
              />
            ) : (
              <StatusCellV1
                status={patient.status}
                statusReason={patient.statusReason}
                statusOtherReason={patient.statusOtherReason}
                statusEndDate={patient.statusEndDate}
                statusStartDate={patient.statusStartDate}
                statusUpdatedAt={patient.statusUpdatedAt}
              />
            )}
          </Table.Td>
        );
      // v2 only: the v1 list passes no columns, so these never render there.
      case 'setup':
        return (
          <Table.Td key={col.id} py={cellPy}>
            <SetupCell patientId={patient.id} status={patient.status} checklist={checklist} />
          </Table.Td>
        );
      case 'device':
        return (
          <Table.Td key={col.id} py={cellPy}>
            <DeviceCell patient={patient} />
          </Table.Td>
        );
      case 'remoteMonitored':
        return (
          <Table.Td key={col.id} py={cellPy}>
            <MonitoringCell
              isRemoteMonitored={patient.isRemoteMonitored}
              isRhythmMonitored={patient.isRhythmMonitored}
            />
          </Table.Td>
        );
      case 'diagnosis':
        return (
          <Table.Td key={col.id} py={cellPy}>
            <DiagnosisCell checklist={checklist} patientId={patient.id} />
          </Table.Td>
        );
      case 'following':
        return (
          <Table.Td key={col.id} py={cellPy}>
            <FollowingCell checklist={checklist} patientId={patient.id} />
          </Table.Td>
        );
      case 'demographics':
        return (
          <Table.Td key={col.id} py={cellPy}>
            <DemographicsCell
              checklist={checklist}
              patientId={patient.id}
              dob={patient.dob}
              mrn={patient.mrn}
            />
          </Table.Td>
        );
      case 'insurance':
        return (
          <Table.Td key={col.id} py={cellPy}>
            <InsuranceCell checklist={checklist} patientId={patient.id} />
          </Table.Td>
        );
      case 'clinic':
        return (
          <Table.Td key={col.id} py={cellPy}>
            {isPatientListV2Enabled ? (
              <ClinicCell clinicName={patient.clinicName} />
            ) : (
              <ClinicCellV1 clinicName={patient.clinicName} />
            )}
          </Table.Td>
        );
      case 'lastTransmission':
        return (
          <Table.Td key={col.id} py={cellPy}>
            {isPatientListV2Enabled ? (
              <TransmissionDateCell
                transmissionAt={patient.lastTransmission?.transmissionAt || null}
              />
            ) : (
              <TransmissionDateCellV1
                transmissionAt={patient.lastTransmission?.transmissionAt || null}
              />
            )}
          </Table.Td>
        );
      case 'lastCompletedReport':
        return (
          <Table.Td key={col.id} py={cellPy}>
            {isPatientListV2Enabled ? (
              <PreviousReportCell report={patient.previousReport} />
            ) : (
              <PreviousReportCellV1 report={patient.previousReport} />
            )}
          </Table.Td>
        );
      case 'openReport':
        return (
          <Table.Td key={col.id} py={cellPy}>
            {isPatientListV2Enabled ? (
              <CurrentReportCell report={patient.currentReport} />
            ) : (
              <CurrentReportCellV1 report={patient.currentReport} />
            )}
          </Table.Td>
        );
    }
  };

  const rowClassName = isPatientListV2Enabled
    ? `${styles.patientRowV2} ${density === 'compact' ? styles.patientRowV2Compact : ''}`
    : styles.patientRow;

  return (
    <Table.Tr ref={hasChecklistColumn ? rowRef : undefined} className={rowClassName}>
      <Table.Td py={cellPy} style={{ verticalAlign: 'middle', width: isV2 ? 40 : undefined }}>
        <Tooltip label="No previous report available" disabled={hasReports} withArrow>
          <div style={{ display: 'inline-block' }}>
            <Checkbox
              checked={isSelected}
              onChange={() => hasReports && onToggle(patient)}
              disabled={!hasReports}
              styles={{ input: { cursor: hasReports ? 'pointer' : 'not-allowed' } }}
            />
          </div>
        </Tooltip>
      </Table.Td>
      <Table.Td py={cellPy}>
        {isPatientListV2Enabled ? (
          <PatientCell patient={patient} />
        ) : (
          <PatientCellV1 patient={patient} />
        )}
      </Table.Td>
      <Table.Td py={cellPy} style={isV2 ? { width: 48 } : { width: 80 }}>
        {isPatientListV2Enabled ? (
          <OptionsCell patient={patient} />
        ) : (
          <OptionsCellV1 patient={patient} />
        )}
      </Table.Td>
      {visibleColumns.map(renderColumn)}
    </Table.Tr>
  );
};
